import { fromCallback } from 'xstate';

import { MuteDetectionPipeline } from '../mute-detection-pipeline';

import {
  type MediaRecorderInput as Input,
  MediaRecorderEvents,
} from './mediaRecorder.types';
import { AudioHealthCheck } from './periodicAudioHealthCheck';

export const mediaRecorder = fromCallback<MediaRecorderEvents, Input>(
  ({ input, sendBack: unsafeSendback, receive }) => {
    const {
      audioPacketSubject,
      supportedAudioFormat,
      deviceId,
      logger,
      MuteDetector,
    } = input;

    const sendBack = unsafeSendback as (evt: MediaRecorderEvents) => void;

    let mediaRecorder: MediaRecorder | null = null;
    let muteDetectionPipeline: MuteDetectionPipeline | null = null;
    let recordingStarted = false;
    let mediaStream: MediaStream | null = null;
    let isMuted = false;
    let muteStartTime: number | null = null;
    // Track audio metrics locally instead of mutating input
    let numAudioPacketsReceived = 0;
    let totalBytesReceived = 0;

    const audioHealthCheck = new AudioHealthCheck({
      logger,
      getMediaStream: () => mediaStream,
      getTotalBytesReceived: () => totalBytesReceived,
      onHealthCheckFailed: (reason: string) => {
        sendBack({
          type: 'recorder.error',
          error: new Error(`Audio health check failed: ${reason}`),
        });
      },
      getAdditionalLogData: () => ({
        mediaRecorderState: mediaRecorder?.state ?? 'MISSING MEDIA RECORDER',
        numAudioPacketsReceived,
        isMuted,
        muteStartTime,
        recordingStarted,
      }),
      isHealthy: () => {
        return mediaRecorder?.state === 'recording'
          ? { healthy: true }
          : {
              healthy: false,
              reason: 'MediaRecorder is not in recording state',
            };
      },
    });

    const onMicrophoneDisconnectDetected = () =>
      sendBack({ type: 'microphone.disconnected' });

    const handleMuteEvent = () => {
      isMuted = true;
      muteStartTime = Date.now();
      logger('warn', 'Microphone is muted.', {
        deviceId,
        totalBytesReceived,
        numAudioPacketsReceived,
      });
      sendBack({ type: 'microphone.muted' });
    };

    const handleUnmuteEvent = () => {
      isMuted = false;
      muteStartTime = null;
      logger('info', 'Microphone is unmuted.', {
        deviceId,
        totalBytesReceived,
        numAudioPacketsReceived,
      });
      sendBack({ type: 'microphone.unmuted' });
    };

    const handleDataAvailable = (event: BlobEvent) => {
      audioPacketSubject?.next({
        data: event.data,
        timestamp: event.timeStamp,
      });

      numAudioPacketsReceived += 1;
      totalBytesReceived += event.data.size;
    };

    const handleMediaRecorderError = (event: Event) => {
      logger('error', 'Error occurred while recording audio.', {
        event,
        numAudioPacketsReceived,
        totalBytesReceived,
      });

      sendBack({
        type: 'recorder.error',
        error: new Error('An error occurred while recording audio.'),
      });
    };

    const stopRecorder = () => {
      audioHealthCheck.stop();

      if (mediaRecorder) {
        mediaRecorder.stop();
        mediaRecorder.removeEventListener('dataavailable', handleDataAvailable);
        mediaRecorder.removeEventListener('error', handleMediaRecorderError);
      }

      window.eluveMediaStreams.getTracks('sessionRecorder').forEach((track) => {
        track.removeEventListener('ended', onMicrophoneDisconnectDetected);
      });

      window.eluveMediaStreams.stopAll();

      if (muteDetectionPipeline) {
        muteDetectionPipeline.stop().catch((error) => {
          logger('error', 'Error stopping mute detection pipeline', {
            error,
          });
        });
      }

      mediaRecorder = null;
      muteDetectionPipeline = null;

      logger('info', 'Audio data received from media recorder', {
        numAudioPacketsReceived,
        totalBytesReceived,
      });

      sendBack({
        type: 'recorder.cleanup_finalized',
      });
    };

    const startRecorder = async () => {
      try {
        window.eluveMediaStreams.stop('sessionRecorder');

        // Getting the media stream from the audio input device
        mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        });

        // Validate MediaStream tracks
        const audioTracks = mediaStream.getAudioTracks();
        if (audioTracks.length === 0) {
          throw new Error('No audio tracks available in MediaStream');
        }

        audioTracks.forEach((track) => {
          if (!track.enabled || track.readyState !== 'live') {
            logger('warn', 'Audio track is not active', {
              enabled: track.enabled,
              readyState: track.readyState,
              label: track.label,
            });
          }
          track.addEventListener('ended', onMicrophoneDisconnectDetected);
        });

        mediaRecorder = new MediaRecorder(mediaStream, {
          mimeType: supportedAudioFormat.mimeType,
        });

        mediaRecorder.addEventListener('dataavailable', handleDataAvailable);
        mediaRecorder.addEventListener('error', handleMediaRecorderError);
        mediaRecorder.addEventListener('start', () => {
          recordingStarted = true;
          logger('info', 'MediaRecorder started successfully', {
            deviceId,
            audioBitsPerSecond: mediaRecorder?.audioBitsPerSecond,
          });
        });

        mediaRecorder.addEventListener('stop', () => {
          logger('info', 'Media recorder stopped', {
            deviceId,
          });
        });

        mediaRecorder.start(100);

        // Set up mute detection
        muteDetectionPipeline = new MuteDetectionPipeline({
          mediaStream,
          MuteDetector,
          onMute: handleMuteEvent,
          onUnmute: handleUnmuteEvent,
          logger,
        });
        muteDetectionPipeline.start();

        window.eluveMediaStreams.set('sessionRecorder', mediaStream);

        audioHealthCheck.start();

        sendBack({
          type: 'recorder.started',
          mediaStream,
        });
      } catch (error) {
        // When the selected device ID is not available, treat it as a disconnection
        if (error instanceof Error && error.name === 'OverconstrainedError') {
          const isDeviceIdConstraint =
            ('constraints' in error && error.constraints === 'deviceId') ||
            ('constraint' in error && error.constraint === 'deviceId');
          if (isDeviceIdConstraint) {
            onMicrophoneDisconnectDetected();
            return;
          }
        }

        sendBack({
          type: 'recorder.error',
          error: new Error(
            `An error occurred while getting stream from audio input device. Error: ${(error as Error)?.message}`,
          ),
        });
      }
    };

    startRecorder();

    receive((event) => {
      if (event.type === 'recorder.stop') {
        stopRecorder();
      }
    });

    return () => {
      stopRecorder();
    };
  },
);
